
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { PlusCircle, Book, FileText, ListChecks, LayoutDashboard } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import ModuleEditor from '@/components/admin/course-builder/ModuleEditor';
import TaxonomyPathVisualizer from '@/components/admin/course-builder/TaxonomyPathVisualizer';
import LessonBrowser from '@/components/admin/course-builder/LessonBrowser';
import ModuleStatus from '@/components/admin/course-builder/ModuleStatus';
import ModuleComments from '@/components/admin/course-builder/ModuleComments';
import { LearningModule } from '@/types/supabase';

const CourseBuilder = () => {
  const { isAdmin, isMentor } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('modules');
  const [selectedModule, setSelectedModule] = useState<LearningModule | null>(null);

  React.useEffect(() => {
    if (!isAdmin() && !isMentor()) {
      navigate('/dashboard');
    }
  }, [isAdmin, isMentor, navigate]);

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight section-header">Course Builder</h1>
          <p className="text-muted-foreground section-subheader">Create and manage your course content</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            <LayoutDashboard className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </div>

      <Tabs 
        defaultValue="modules" 
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="modules" className="flex items-center gap-2">
            <Book className="h-4 w-4" />
            <span>Modules</span>
          </TabsTrigger>
          <TabsTrigger value="lessons" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span>Lessons</span>
          </TabsTrigger>
          <TabsTrigger value="taxonomy" className="flex items-center gap-2">
            <ListChecks className="h-4 w-4" />
            <span>Taxonomy Path</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="modules" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="card-title">Module Management</CardTitle>
                <CardDescription>
                  Create and manage learning modules for the course
                </CardDescription>
              </div>
              <Button onClick={() => setSelectedModule(null)}>
                <PlusCircle className="mr-2 h-4 w-4" />
                New Module
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="md:col-span-2">
                  <ModuleEditor 
                    moduleId={selectedModule?.id || undefined} 
                    onModuleSelected={(module) => {
                      setSelectedModule(module);
                    }}
                    onModuleCreated={(module) => {
                      setSelectedModule(module);
                      setActiveTab('lessons');
                    }}
                  />
                </div>
                
                <div className="space-y-4">
                  {selectedModule && (
                    <>
                      <ModuleStatus moduleId={selectedModule.id} />
                      <ModuleComments moduleId={selectedModule.id} />
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="lessons" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="card-title">Lesson Management</CardTitle>
                <CardDescription>
                  Create and manage lessons for your modules
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {selectedModule ? (
                <LessonBrowser moduleId={selectedModule.id} moduleName={selectedModule.title} />
              ) : (
                <div className="text-center py-6">
                  <p className="text-muted-foreground">Please select a module first</p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => setActiveTab('modules')}
                  >
                    Back to Modules
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="taxonomy" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="card-title">Taxonomy Learning Path</CardTitle>
                <CardDescription>
                  Visualize your course progression through Bloom's Taxonomy
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              <TaxonomyPathVisualizer />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CourseBuilder;
